/**
 * cameraFov.ts — aspect compensation for the chase camera (M122), PURE + tested.
 *
 * THE PROBLEM. three.js `PerspectiveCamera.fov` is the VERTICAL field of view,
 * and nothing compensated for aspect, so how much of the ROAD you could see was
 * decided entirely by the shape of your screen:
 *
 *   phone landscape  812x375  aspect 2.17 -> 111 deg horizontal  (the tuned case)
 *   desktop         1280x800  aspect 1.60 ->  94 deg
 *   iPad 12.9" land 1366x1024 aspect 1.33 ->  84 deg
 *   iPad 12.9" port 1024x1366 aspect 0.75 ->  54 deg
 *
 * That is not cosmetic. Standings rank by BALL progress, so seeing your ball is
 * the core loop, and 54 degrees is a real competitive handicap. It is reachable
 * without warning too: the rotate-phone nudge deliberately exempts tablets
 * (they are legitimately played either way), so portrait tablet racing is an
 * unguided state.
 *
 * THE OWNER'S CONSTRAINT: tablets are SECONDARY and must never jeopardise the
 * game as it plays today. So this function is built to be INERT on the primary
 * platforms rather than "correct" everywhere:
 *
 *   - At aspect >= PRESERVE_ASPECT it returns the base FOV UNCHANGED. Every
 *     phone in landscape (2.0+) and the standard desktop window (1.6) therefore
 *     render byte-identically to before. This is the whole safety argument.
 *   - Below that it widens the VERTICAL fov just enough to hold the horizontal
 *     view at what 1.6 already gave (~94 deg), so a narrow screen stops losing
 *     road. It never widens beyond what desktop players already see, so this
 *     can only ever restore view, never invent a new wider look.
 *   - MAX_FOV caps the result so a tall portrait screen gets a better view
 *     without tipping into fisheye. Portrait stays narrower than landscape,
 *     which is honest: a portrait window genuinely shows less road.
 *
 * DISPLAY ONLY. Camera framing is not simulation state, so this needs no
 * SIM_VERSION bump and cannot desync online play.
 */

/**
 * At or above this aspect, nothing changes AT ALL, and it is also the reference
 * the compensation aims at. Chosen as the standard desktop window
 * (1280x800 = 1.6) so both primary platforms — phones in landscape and desktop
 * — are provably untouched.
 */
export const PRESERVE_ASPECT = 1.6;

/**
 * Compensation reaches full strength at or below this aspect, and ramps
 * linearly to nothing between here and PRESERVE_ASPECT.
 *
 * Why a ramp rather than a hard threshold: a step would mean that dragging a
 * desktop window through the boundary visibly jumped the camera. The ramp also
 * makes the inert guarantee EXACT rather than merely tiny — a 1180x740 window
 * (aspect 1.595) was drifting by 0.18 degrees under a hard threshold at 1.6,
 * which is imperceptible but is not "unchanged", and unchanged is the promise.
 *
 * 1.44 sits just under the iPad 10.9" landscape aspect (1.439), so every iPad
 * from 10.9" upward gets the full fix while near-desktop shapes (iPad Mini
 * landscape at 1.52, 3:2 windows) get a proportionate part of it.
 */
export const FULL_ASPECT = 1.44;

/**
 * Ceiling on the compensated vertical FOV. Without it a portrait tablet solves
 * to ~114 deg vertical, which reads as a fisheye lens rather than a fix.
 */
export const MAX_FOV = 82;

const DEG = Math.PI / 180;

/**
 * The vertical FOV to use for a given base FOV and viewport aspect.
 *
 * @param baseFov  the tuned vertical FOV in degrees (tuning.camera.fovBase,
 *                 plus any live boost punch — the punch rides along so a boost
 *                 still widens the view on every device).
 * @param aspect   viewport width / height.
 */
export function fovForAspect(baseFov: number, aspect: number): number {
  // Guard the degenerate cases a zero-size canvas can produce mid-resize.
  if (!Number.isFinite(aspect) || aspect <= 0) return baseFov;
  if (aspect >= PRESERVE_ASPECT) return baseFov;

  // The horizontal view the base FOV yields at the preserve point — the most
  // this function is ever allowed to hand back.
  const halfH = Math.atan(Math.tan((baseFov / 2) * DEG) * PRESERVE_ASPECT);
  // The vertical FOV that reproduces that horizontal view at THIS aspect.
  const full = Math.min(MAX_FOV, (2 * Math.atan(Math.tan(halfH) / aspect)) / DEG);

  // Ramp the correction in, so there is no step to see while resizing and the
  // "desktop is untouched" guarantee is exact rather than approximate.
  const t =
    aspect <= FULL_ASPECT ? 1 : (PRESERVE_ASPECT - aspect) / (PRESERVE_ASPECT - FULL_ASPECT);

  // Never below the base: the correction may only ever restore view.
  return Math.max(baseFov, baseFov + (full - baseFov) * t);
}
